/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import axios from "axios";
import { 
  LayoutDashboard, 
  Database, 
  CalendarDays, 
  Sparkles, 
  FileText, 
  BadgeDollarSign, 
  MessageSquare,
  Mail,
  Upload,
  Download,
  LogOut,
  X,
  Plus,
  Users,
  Trash2,
  Send,
  Lock,
  CheckSquare
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PatientsTab from "./booking/PatientsTab";
import ServicesTab from "./booking/ServicesTab";
import ProvidersTab from "./booking/ProvidersTab";
import AppointmentsTab from "./booking/AppointmentsTab";
import { downloadFile } from "@/lib/downloadFile";
import ClientPortalMessages from "@/inbox/ClientPortalMessages";

// "" (empty) is a valid, intentional value: the production build uses relative
// /api URLs behind Caddy (same origin). `||` would treat "" as unset and send
// the browser to localhost:8001; `??` only falls back when it is truly unset.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "http://localhost:8001";
const CLIENT_BOOKING_BASE = "/api/client/v1/booking";
const CLIENT_TEAM_BASE = "/api/client/v1/team";
const CLIENT_ROLES = ["owner", "manager", "staff"];

// Client Portal Main Component
export default function ClientPortal() {
  const [token, setToken] = useState(localStorage.getItem("client_token"));
  const [user, setUser] = useState(null);
  const [active, setActive] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);

  const api = axios.create({
    baseURL: BACKEND_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const showNotice = (message, type = "info") => {
    setNotice({ message, type });
    setTimeout(() => setNotice(null), 5000);
  };

  const logout = () => {
    localStorage.removeItem("client_token");
    setToken(null);
    setUser(null);
  };

  // Client sessions expire (and end on a password change) - a 401 on an
  // authenticated call means the session is over, so return to sign-in
  // rather than leaving every panel erroring.
  api.interceptors.response.use(
    (res) => res,
    (err) => {
      if (token && err.response?.status === 401) logout();
      return Promise.reject(err);
    }
  );

  // The login form is the only place that sets `user`, but the token
  // survives in localStorage - after a reload, or right after activation
  // (which stores just the token), user.role was unknown and owners lost
  // the team invite/role controls. Re-fetch it from /client/me.
  useEffect(() => {
    if (!token || user) return;
    axios.get(`${BACKEND_URL}/api/client/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUser(res.data.user || null))
      .catch((err) => {
        if (err.response?.status === 401) logout();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  if (!token) {
    return <ClientLogin setToken={setToken} setUser={setUser} showNotice={showNotice} />;
  }

  return (
    <div className="app-container" data-testid="client-portal">
      {notice && (
        <div className={`toast-notice ${notice.type}`} data-testid="toast-notice">
          <span>{notice.message}</span>
          <button onClick={() => setNotice(null)}><X size={16} /></button>
        </div>
      )}
      
      <ClientSidebar active={active} setActive={setActive} user={user} logout={logout} />
      
      <main className="app-main">
        <ClientModuleRenderer
          active={active}
          api={api}
          token={token}
          setToken={setToken}
          user={user}
          showNotice={showNotice}
          loading={loading}
          setLoading={setLoading}
        />
      </main>
    </div>
  );
}

// Client Login Component
function ClientLogin({ setToken, setUser, showNotice }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/client/auth/login`, { email, password });
      localStorage.setItem("client_token", res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      showNotice("Login successful!", "success");

      if (res.data.user.temporary_password) {
        showNotice("⚠️ You are using a temporary password. Please change it under Account Settings.", "warning");
      }
    } catch (err) {
      showNotice(err.response?.data?.detail || "Login failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setResetSubmitting(true);
    setResetMessage("");
    try {
      await axios.post(`${BACKEND_URL}/api/client/request-password-reset`, { email: resetEmail });
      setResetMessage("If this email exists, a reset link has been sent.");
    } catch (err) {
      setResetMessage(err.response?.data?.detail || "If this email exists, a reset link has been sent.");
    } finally {
      setResetSubmitting(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="login-container" data-testid="client-forgot-password-page">
        <div className="login-panel glass-panel">
          <div className="brand-lockup" style={{marginBottom: "2rem"}}>
            <h2>MedSpaBookings</h2>
            <span>Reset Your Password</span>
          </div>

          <form onSubmit={handleRequestReset}>
            <label>
              Email
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                data-testid="client-forgot-password-email"
              />
            </label>

            <button
              type="submit"
              className="primary-btn"
              disabled={resetSubmitting}
              data-testid="client-forgot-password-submit"
            >
              {resetSubmitting ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          {resetMessage && (
            <p style={{marginTop: "1rem", fontSize: "0.875rem"}} data-testid="client-forgot-password-message">
              {resetMessage}
            </p>
          )}

          <p style={{marginTop: "1.5rem", fontSize: "0.875rem"}}>
            <button
              type="button"
              className="ghost-btn small"
              onClick={() => { setShowForgotPassword(false); setResetMessage(""); }}
              data-testid="client-forgot-password-back-to-login"
            >
              Back to login
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container" data-testid="client-login-page">
      <div className="login-panel glass-panel">
        <div className="brand-lockup" style={{marginBottom: "2rem"}}>
          <h2>MedSpaBookings</h2>
          <span>Client Portal</span>
        </div>
        
        <form onSubmit={handleLogin}>
          <label>
            Email
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              data-testid="client-login-email"
            />
          </label>
          
          <label>
            Password
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              data-testid="client-login-password"
            />
          </label>

          <p style={{margin: "0.5rem 0 1rem", textAlign: "right"}}>
            <button
              type="button"
              className="ghost-btn small"
              onClick={() => setShowForgotPassword(true)}
              data-testid="client-login-forgot-password-link"
            >
              Forgot password?
            </button>
          </p>

          <button
            type="submit"
            className="primary-btn"
            disabled={loading}
            data-testid="client-login-submit"
          >
            {loading ? "Logging in..." : "Login to Client Portal"}
          </button>
        </form>
        
        <p style={{marginTop: "1rem", fontSize: "0.875rem", color: "var(--text-secondary)"}}>
          Need help? Contact your account manager.
        </p>
        <p style={{marginTop: "0.75rem", fontSize: "0.8rem", color: "var(--text-secondary)"}} data-testid="client-login-legal-links">
          <a href="/terms">Terms of Service</a> &middot; <a href="/privacy">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}

// Client Sidebar
function ClientSidebar({ active, setActive, user, logout }) {
  const NAV_ITEMS = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "leads", label: "My Leads", icon: Database },
    { id: "tasks", label: "Onboarding Tasks", icon: CheckSquare },
    { id: "appointments", label: "Appointments", icon: CalendarDays },
    { id: "audits", label: "AI Audits", icon: Sparkles },
    { id: "proposals", label: "Proposals", icon: FileText },
    { id: "billing", label: "Billing", icon: BadgeDollarSign },
    { id: "documents", label: "Documents", icon: Upload },
    { id: "support", label: "Support", icon: MessageSquare },
    { id: "messages", label: "Messages", icon: Mail },
    { id: "team", label: "Team", icon: Users },
    { id: "settings", label: "Account Settings", icon: Lock },
  ];

  return (
    <aside className="app-sidebar">
      <div className="rail-top">
        <div className="brand-lockup">
          <div className="brand-mini">CP</div>
          <div>
            <strong>Client Portal</strong>
            <span>{user?.client_name || "MedSpaBookings"}</span>
          </div>
        </div>
      </div>

      <div className="role-card">
        <p>Logged in as</p>
        <strong>{user?.email || "Client"}</strong>
        <span>{user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "Client User"}</span>
      </div>

      <nav>
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className={`nav-item ${active === item.id ? "active" : ""}`}
              onClick={() => setActive(item.id)}
              data-testid={`client-nav-${item.id}`}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <button className="nav-item" onClick={logout} data-testid="client-logout">
        <LogOut size={18} />
        <span>Logout</span>
      </button>
    </aside>
  );
}

// Module Renderer
function ClientModuleRenderer({ active, api, token, setToken, user, showNotice, loading, setLoading }) {
  if (active === "dashboard") return <ClientDashboard api={api} showNotice={showNotice} />;
  if (active === "leads") return <ClientLeads api={api} showNotice={showNotice} />;
  if (active === "tasks") return <ClientTasks api={api} showNotice={showNotice} />;
  if (active === "appointments") return <ClientBookingCenter token={token} />;
  if (active === "audits") return <ClientAudits api={api} showNotice={showNotice} />;
  if (active === "proposals") return <ClientProposals api={api} showNotice={showNotice} user={user} />;
  if (active === "billing") return <ClientBilling api={api} showNotice={showNotice} />;
  if (active === "documents") return <ClientDocuments api={api} showNotice={showNotice} />;
  if (active === "support") return <ClientSupport api={api} showNotice={showNotice} />;
  if (active === "messages") return <ClientPortalMessages api={api} showNotice={showNotice} />;
  if (active === "team") return <ClientTeam api={api} user={user} showNotice={showNotice} />;
  if (active === "settings") return <ClientSettings api={api} showNotice={showNotice} onToken={(t) => { localStorage.setItem("client_token", t); setToken(t); }} />;
  return <div>Module not found</div>;
}

// Client Dashboard
function billingDateLabel(value) {
  if (!value) return "N/A";
  const date = typeof value === "number" ? new Date(value * 1000) : new Date(value);
  return isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
}

function ClientDashboard({ api, showNotice }) {
  const [data, setData] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);

  useEffect(() => {
    api.get("/api/client/dashboard")
      .then(res => setData(res.data))
      .catch(err => showNotice(err.response?.data?.detail || "Failed to load dashboard", "error"));
    api.get("/api/client/me")
      .then(res => setAccountStatus(res.data.account_status))
      .catch(err => showNotice(err.response?.data?.detail || "Failed to load account status", "error"));
  }, []);

  if (!data) return <div>Loading...</div>;

  return (
    <div className="page-stack" data-testid="client-dashboard">
      <section className="hero-panel ceo-hero glass-panel">
        <div>
          <p className="eyebrow">Welcome Back</p>
          <h2>{data.client?.clinic_name || "Client Portal"}</h2>
          <div className="hero-stats">
            <span>{data.stats?.leads || 0} Leads</span>
            <span>{data.stats?.audits || 0} Audits</span>
            <span>{data.stats?.proposals || 0} Proposals</span>
            <span>{data.stats?.documents || 0} Documents</span>
          </div>
        </div>
      </section>

      <section className="revenue-stat-grid">
        <StatTile label="Total Leads" value={data.stats?.leads || 0} />
        <StatTile label="AI Audits" value={data.stats?.audits || 0} />
        <StatTile label="Proposals" value={data.stats?.proposals || 0} />
        <StatTile label="Documents" value={data.stats?.documents || 0} />
      </section>

      {accountStatus && (
        <section className="panel glass-panel" data-testid="client-account-status">
          <div className="panel-header"><h3>Account Status</h3></div>
          <div className="revenue-stat-grid">
            <StatTile label="Setup Fee" value={accountStatus.setup_fee_paid ? "Paid" : "Pending"} />
            <StatTile label="Subscription" value={accountStatus.subscription_active ? "Active" : "Inactive"} />
            {accountStatus.subscription_active && (
              <>
                <StatTile label="Monthly Amount" value={accountStatus.monthly_amount || 0} formatter={(v) => `$${v.toFixed(2)}`} />
                <StatTile label="Next Billing" value={billingDateLabel(accountStatus.next_billing_date)} />
              </>
            )}
            <StatTile label="Onboarding Progress" value={accountStatus.onboarding_progress?.percentage ?? 0} formatter={(v) => `${v}%`} />
          </div>
        </section>
      )}

      {data.recent_audits?.length > 0 && (
        <section className="panel glass-panel">
          <div className="panel-header"><h3>Recent AI Audits</h3></div>
          <div className="compact-list">
            {data.recent_audits.map(audit => (
              <div key={audit.id} className="compact-row">
                <Sparkles size={16} />
                <div>
                  <strong>{audit.clinic_name}</strong>
                  <span>Overall Score: {audit.overall_medspa_growth_score ?? "N/A"}{audit.overall_medspa_growth_score != null && "/100"} •{new Date(audit.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// Client Leads
function ClientLeads({ api, showNotice }) {
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    api.get("/api/client/leads")
      .then(res => setLeads(res.data.leads || []))
      .catch(err => showNotice(err.response?.data?.detail || "Failed to load leads", "error"));
  }, []);

  return (
    <div className="page-stack" data-testid="client-leads">
      <section className="page-header">
        <h2>My Leads</h2>
      </section>
      
      <section className="panel glass-panel">
        {leads.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Lead Name</th>
                <th>Status</th>
                <th>Source</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id}>
                  <td>{lead.clinic_name || lead.name}</td>
                  <td><span className="status-badge">{lead.status}</span></td>
                  <td>{lead.source || lead.lead_source}</td>
                  <td>{new Date(lead.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-cell">No leads yet</div>
        )}
      </section>
    </div>
  );
}

// Client Tasks (onboarding checklist)
const TASK_COMPLETE_STATUSES = ["completed", "Done"];

function ClientTasks({ api, showNotice }) {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ total: 0, completed: 0, pending: 0 });

  useEffect(() => {
    api.get("/api/client/tasks")
      .then(res => {
        setTasks(res.data.tasks || []);
        setSummary({ total: res.data.total || 0, completed: res.data.completed || 0, pending: res.data.pending || 0 });
      })
      .catch(err => showNotice(err.response?.data?.detail || "Failed to load tasks", "error"));
  }, []);

  return (
    <div className="page-stack" data-testid="client-tasks">
      <section className="page-header">
        <h2>Onboarding Tasks</h2>
      </section>

      <section className="revenue-stat-grid">
        <StatTile label="Total Tasks" value={summary.total} />
        <StatTile label="Completed" value={summary.completed} />
        <StatTile label="Pending" value={summary.pending} />
      </section>

      <section className="panel glass-panel">
        {tasks.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Due Date</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(task => (
                <tr key={task.id}>
                  <td>{task.title}</td>
                  <td>{task.priority || "—"}</td>
                  <td>
                    <span className={`status-badge ${TASK_COMPLETE_STATUSES.includes(task.status) ? "success" : ""}`}>
                      {task.status || "—"}
                    </span>
                  </td>
                  <td>{task.due_date ? new Date(task.due_date).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-cell">No onboarding tasks yet</div>
        )}
      </section>
    </div>
  );
}

// Client Booking Center - the real scheduling domain (patients, treatment
// menu, providers, conflict-aware appointments), scoped to this clinic's
// own login. No clinic picker here, unlike the internal-staff BookingCenter:
// this token only ever resolves to the one clinic it belongs to.
function ClientBookingCenter({ token }) {
  const [activeTab, setActiveTab] = useState("appointments");

  return (
    <div className="page-stack" data-testid="client-appointments">
      <section className="page-header">
        <h2>Appointments</h2>
      </section>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="glass-panel flex-wrap">
          <TabsTrigger value="appointments" data-testid="client-tab-appointments">Appointments</TabsTrigger>
          <TabsTrigger value="patients" data-testid="client-tab-patients">Patients</TabsTrigger>
          <TabsTrigger value="services" data-testid="client-tab-services">Services</TabsTrigger>
          <TabsTrigger value="providers" data-testid="client-tab-providers">Providers</TabsTrigger>
        </TabsList>
        <TabsContent value="appointments">
          <AppointmentsTab basePath={CLIENT_BOOKING_BASE} token={token} />
        </TabsContent>
        <TabsContent value="patients">
          <PatientsTab basePath={CLIENT_BOOKING_BASE} token={token} />
        </TabsContent>
        <TabsContent value="services">
          <ServicesTab basePath={CLIENT_BOOKING_BASE} token={token} />
        </TabsContent>
        <TabsContent value="providers">
          <ProvidersTab basePath={CLIENT_BOOKING_BASE} token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Client Audits
function ClientAudits({ api, showNotice }) {
  const [audits, setAudits] = useState([]);
  useEffect(() => {
    api.get("/api/client/audits").then(res => setAudits(res.data.audits || [])).catch(err => showNotice("Failed to load audits", "error"));
  }, []);
  
  const downloadPDF = async (auditId) => {
    const token = localStorage.getItem("client_token");
    try {
      await downloadFile(`${BACKEND_URL}/api/client/audits/${auditId}/pdf`, token, `Audit_${auditId.slice(0, 8)}.pdf`);
    } catch (err) {
      showNotice(err.message, "error");
    }
  };
  
  return <div className="page-stack" data-testid="client-audits"><section className="page-header"><h2>AI Audit Reports</h2></section><section className="panel glass-panel">{audits.length > 0 ? <div className="compact-list">{audits.map(audit => <div key={audit.id} className="compact-row"><Sparkles size={16}/><div><strong>{audit.clinic_name}</strong><span>Overall Score: {audit.overall_medspa_growth_score || "N/A"}/100 • {new Date(audit.created_at).toLocaleDateString()}</span></div><button className="primary-btn small" data-testid={`client-audit-pdf-${audit.id}`} onClick={() => downloadPDF(audit.id)}><Download size={14}/> PDF</button></div>)}</div> : <div className="empty-cell">No audits yet</div>}</section></div>;
}

// Client Proposals
function ClientProposals({ api, showNotice, user }) {
  const [proposals, setProposals] = useState([]);
  const [accepting, setAccepting] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  // Accepting is a binding commitment - the drawer below says so - so it
  // is owner-only on the backend (which still enforces this regardless);
  // hiding the button for non-owners is purely UI polish, same as the
  // Team page's canManageRoles convention.
  const canAccept = (user?.role || "owner") === "owner";
  
  useEffect(() => {
    api.get("/api/client/proposals").then(res => setProposals(res.data.proposals || [])).catch(err => showNotice("Failed to load proposals", "error"));
  }, []);
  
  const downloadPDF = async (proposalId) => {
    const token = localStorage.getItem("client_token");
    try {
      await downloadFile(`${BACKEND_URL}/api/client/proposals/${proposalId}/pdf`, token, `Proposal_${proposalId.slice(0, 8)}.pdf`);
    } catch (err) {
      showNotice(err.message, "error");
    }
  };
  
  const acceptProposal = async (proposalId) => {
    setAccepting(proposalId);
    try {
      const res = await api.post(`/api/client/proposals/${proposalId}/accept`);
      if (res.data.checkout_url) {
        // A tracked Stripe Checkout Session created for this proposal.
        showNotice("Proposal accepted! Redirecting to secure payment...", "success");
        window.location.href = res.data.checkout_url;
        return;
      }
      showNotice(res.data.payment_message || "Proposal accepted. Your advisor will follow up with payment details.", "info");
      setAccepting(null);
      api.get("/api/client/proposals").then(r => setProposals(r.data.proposals || [])).catch(() => {});
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to accept proposal", "error");
      setAccepting(null);
    }
  };
  
  return <div className="page-stack" data-testid="client-proposals"><section className="page-header"><h2>Proposals</h2></section><section className="panel glass-panel">{proposals.length > 0 ? <div className="compact-list">{proposals.map(prop => <div key={prop.id} className="compact-row"><FileText size={16}/><div><strong>{prop.offer_name}</strong><span>Status: {prop.status} • Setup: ${prop.setup_fee.toLocaleString()} • Monthly: ${prop.monthly_management}/mo • {new Date(prop.created_at).toLocaleDateString()}</span></div><div className="button-row"><button className="primary-btn small" data-testid={`client-proposal-pdf-${prop.id}`} onClick={() => downloadPDF(prop.id)}><Download size={14}/> PDF</button>{["Sent", "Accepted"].includes(prop.status) && canAccept && <button className="primary-btn small" data-testid={`client-proposal-accept-${prop.id}`} onClick={() => setShowConfirm(prop.id)} disabled={accepting === prop.id}>{accepting === prop.id ? "Processing..." : prop.status === "Accepted" ? "Complete Payment" : "Accept Proposal"}</button>}</div></div>)}</div> : <div className="empty-cell">No proposals yet</div>}</section>{showConfirm && <div className="drawer-backdrop" onClick={() => setShowConfirm(null)}><div className="record-drawer glass-panel" onClick={e => e.stopPropagation()} style={{maxWidth: "500px"}}><div className="drawer-header"><h2>Accept Proposal</h2><button className="icon-button" onClick={() => setShowConfirm(null)}><X size={18}/></button></div><div><p>By accepting this proposal, you agree to proceed with MedSpaBookings Growth System.</p><p style={{marginTop: "16px"}}>You will be redirected to complete the <strong>$12,500 setup fee payment</strong> via Stripe.</p></div><div className="drawer-footer"><button className="ghost-btn" onClick={() => setShowConfirm(null)}>Cancel</button><button className="primary-btn" data-testid="client-proposal-confirm-accept" onClick={() => { setShowConfirm(null); acceptProposal(showConfirm); }}>Continue to Payment</button></div></div></div>}</div>;
}

// Client Billing
function ClientBilling({ api, showNotice }) {
  const [billing, setBilling] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  useEffect(() => {
    api.get("/api/client/billing").then(res => setBilling(res.data)).catch(err => showNotice("Failed to load billing", "error"));
  }, []);

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await api.post("/api/client/billing/portal-session");
      window.location.href = res.data.url;
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to open billing portal", "error");
      setOpeningPortal(false);
    }
  };

  if (!billing) return <div>Loading...</div>;
  return <div className="page-stack" data-testid="client-billing"><section className="page-header"><h2>Billing History</h2><button className="primary-btn" data-testid="client-billing-portal-button" onClick={openBillingPortal} disabled={openingPortal}>{openingPortal ? "Opening..." : "Manage Payment Method"}</button></section><section className="revenue-stat-grid"><StatTile label="Total Paid (net of refunds)" value={billing.total_paid} formatter={(v) => `$${v.toFixed(2)}`} />{billing.total_refunded > 0 && <StatTile label="Refunded" value={billing.total_refunded} formatter={(v) => `$${v.toFixed(2)}`} />}<StatTile label="Transactions" value={billing.total} /></section><section className="panel glass-panel">{billing.transactions?.length > 0 ? <table><thead><tr><th>Package</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead><tbody>{billing.transactions.map(tx => <tr key={tx.id}><td>{tx.package_name}</td><td>${tx.amount}</td><td><span className={`status-badge ${tx.payment_status === "paid" ? "success" : "warning"}`}>{tx.payment_status}</span>{tx.refunded_cents > 0 && <div style={{fontSize: "0.75rem"}}>${(tx.refunded_cents / 100).toFixed(2)} refunded</div>}</td><td>{new Date(tx.created_at).toLocaleDateString()}</td></tr>)}</tbody></table> : <div className="empty-cell">No billing history yet</div>}</section></div>;
}

// Client Documents
function ClientDocuments({ api, showNotice }) {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = () => {
    api.get("/api/client/documents").then(res => setDocuments(res.data.documents || [])).catch(err => showNotice("Failed to load documents", "error"));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post("/api/client/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      showNotice("Document uploaded successfully", "success");
      loadDocuments();
    } catch (err) {
      showNotice(err.response?.data?.detail || "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      // A plain <a href> can't carry the Authorization header this route
      // needs, so the file is fetched as a blob and handed to the browser
      // as a temporary object URL instead.
      const res = await api.get(`/api/client/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.filename || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showNotice(err.response?.data?.detail || "Download failed", "error");
    }
  };

  return (
    <div className="page-stack" data-testid="client-documents">
      <section className="page-header">
        <h2>Documents</h2>
        <label className="primary-btn" style={{cursor: "pointer"}}>
          <Upload size={16}/> Upload Document
          <input type="file" style={{display: "none"}} onChange={handleUpload} disabled={uploading} data-testid="client-document-upload"/>
        </label>
      </section>
      
      <section className="panel glass-panel">
        {documents.length > 0 ? (
          <div className="compact-list">
            {documents.map(doc => (
              <div key={doc.id} className="compact-row">
                <FileText size={16}/>
                <div>
                  <strong>{doc.filename}</strong>
                  <span>{(doc.size / 1024).toFixed(2)} KB • {new Date(doc.created_at).toLocaleDateString()}</span>
                </div>
                <button className="ghost-btn small" onClick={() => handleDownload(doc)} data-testid={`client-document-download-${doc.id}`}>
                  <Download size={14}/> Download
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-cell">No documents yet. Upload your first document above.</div>
        )}
      </section>
    </div>
  );
}

// Client Support
function ClientSupport({ api, showNotice }) {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "Medium" });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = () => {
    api.get("/api/client/support").then(res => setRequests(res.data.requests || [])).catch(err => showNotice("Failed to load support requests", "error"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/client/support", { data: form });
      showNotice("Support request submitted successfully", "success");
      setShowForm(false);
      setForm({ subject: "", message: "", priority: "Medium" });
      loadRequests();
    } catch (err) {
      showNotice("Failed to submit request", "error");
    }
  };

  return (
    <div className="page-stack" data-testid="client-support">
      <section className="page-header">
        <h2>Support Requests</h2>
        <button className="primary-btn" onClick={() => setShowForm(!showForm)} data-testid="client-support-new">
          <Plus size={16}/> {showForm ? "Cancel" : "New Request"}
        </button>
      </section>

      {showForm && (
        <section className="panel glass-panel">
          <form onSubmit={handleSubmit}>
            <label>Subject<input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} required data-testid="client-support-subject"/></label>
            <label>Message<textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required rows={5} data-testid="client-support-message"/></label>
            <label>Priority<select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})}><option>Low</option><option>Medium</option><option>High</option></select></label>
            <button type="submit" className="primary-btn" data-testid="client-support-submit">Submit Request</button>
          </form>
        </section>
      )}

      <section className="panel glass-panel">
        {requests.length > 0 ? (
          <div className="compact-list">
            {requests.map(req => (
              <div key={req.id} className="compact-row">
                <MessageSquare size={16}/>
                <div>
                  <strong>{req.subject}</strong>
                  <span>Status: {req.status} • Priority: {req.priority} • {new Date(req.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-cell">No support requests yet</div>
        )}
      </section>
    </div>
  );
}

// Client Team (team/role management)
function ClientTeam({ api, user, showNotice }) {
  const [members, setMembers] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "staff" });
  const [inviting, setInviting] = useState(false);

  // Controls that would just 403 (or, for removing your own row, 400) on
  // the backend are hidden entirely rather than shown-and-disabled - the
  // backend still enforces every one of these, this is purely UI polish.
  const canInvite = user?.role === "owner" || user?.role === "manager";
  const canManageRoles = user?.role === "owner";

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = () => {
    api
      .get(CLIENT_TEAM_BASE)
      .then((res) => setMembers(res.data.items || []))
      .catch((err) => showNotice(err.response?.data?.detail || "Failed to load team", "error"));
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await api.post(CLIENT_TEAM_BASE, inviteForm);
      showNotice(`Invited ${res.data.email} as ${res.data.role}. An email with a link to set their password has been sent.`, "success");
      setShowInviteForm(false);
      setInviteForm({ email: "", role: "staff" });
      loadTeam();
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to invite team member", "error");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId, role) => {
    try {
      await api.put(`${CLIENT_TEAM_BASE}/${memberId}`, { role });
      showNotice("Role updated", "success");
      loadTeam();
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to update role", "error");
    }
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm("Remove this team member? They will no longer be able to log in.")) return;
    try {
      await api.delete(`${CLIENT_TEAM_BASE}/${memberId}`);
      showNotice("Team member removed", "success");
      loadTeam();
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to remove team member", "error");
    }
  };

  const handleResend = async (memberId) => {
    try {
      await api.post(`${CLIENT_TEAM_BASE}/${memberId}/resend-activation`);
      showNotice("Activation email resent", "success");
    } catch (err) {
      showNotice(err.response?.data?.detail || "Failed to resend activation email", "error");
    }
  };

  return (
    <div className="page-stack" data-testid="client-team">
      <section className="page-header">
        <h2>Team</h2>
        {canInvite && (
          <button
            className="primary-btn"
            onClick={() => setShowInviteForm(!showInviteForm)}
            data-testid="client-team-invite-toggle"
          >
            <Plus size={16} /> {showInviteForm ? "Cancel" : "Invite"}
          </button>
        )}
      </section>

      {showInviteForm && canInvite && (
        <section className="panel glass-panel">
          <form onSubmit={handleInvite}>
            <label>
              Email
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                data-testid="client-team-invite-email"
              />
            </label>
            <label>
              Role
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                data-testid="client-team-invite-role"
              >
                {/* A manager can invite, but only an owner can invite
                    another owner - the backend 403s otherwise, so a
                    manager never sees "Owner" as a choice here. */}
                {CLIENT_ROLES.filter((role) => role !== "owner" || user?.role === "owner").map((role) => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="primary-btn" disabled={inviting} data-testid="client-team-invite-submit">
              {inviting ? "Inviting..." : "Send Invite"}
            </button>
          </form>
        </section>
      )}

      <section className="panel glass-panel">
        {members.length > 0 ? (
          <div className="compact-list">
            {members.map((member) => (
              <div key={member.id} className="compact-row" data-testid={`client-team-row-${member.id}`}>
                <Users size={16} />
                <div>
                  <strong>{member.email}</strong>
                  <span>
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    {member.id === user?.id ? " • You" : ""}
                    {member.activated === false ? " • Pending activation" : ""}
                  </span>
                </div>
                {canInvite && member.activated === false && (
                  <div className="button-row">
                    <button
                      className="ghost-btn"
                      onClick={() => handleResend(member.id)}
                      data-testid={`client-team-resend-${member.id}`}
                    >
                      <Send size={14} /> Resend
                    </button>
                  </div>
                )}
                {canManageRoles && (
                  <div className="button-row">
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member.id, e.target.value)}
                      data-testid={`client-team-role-select-${member.id}`}
                    >
                      {CLIENT_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>
                    {member.id !== user?.id && (
                      <button
                        className="icon-button"
                        onClick={() => handleRemove(member.id)}
                        data-testid={`client-team-remove-${member.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-cell">No team members yet</div>
        )}
      </section>
    </div>
  );
}

// Client Settings (self-service password change)
function ClientSettings({ api, showNotice, onToken }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      showNotice("New password and confirmation do not match", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/api/client/auth/update-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      // The change ends every earlier session, this one included; the
      // response carries a fresh token so this user stays signed in.
      if (res.data?.token) onToken?.(res.data.token);
      showNotice("Password updated successfully", "success");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string" ? detail : (detail?.errors || []).join(", ") || detail?.message || "Failed to update password";
      showNotice(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-stack" data-testid="client-settings">
      <section className="page-header">
        <h2>Account Settings</h2>
      </section>

      <section className="panel glass-panel">
        <div className="panel-header"><h3>Change Password</h3></div>
        <form onSubmit={handleSubmit}>
          <label>
            Current Password
            <input
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              required
              data-testid="settings-current-password-input"
            />
          </label>
          <label>
            New Password
            <input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              required
              data-testid="settings-new-password-input"
            />
          </label>
          <label>
            Confirm New Password
            <input
              type="password"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              required
              data-testid="settings-confirm-password-input"
            />
          </label>
          <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
            Must be at least 8 characters, with an uppercase letter, a lowercase letter, and a number.
          </p>
          <button
            type="submit"
            className="primary-btn"
            disabled={saving}
            data-testid="settings-save-password-button"
          >
            {saving ? "Updating..." : "Update Password"}
          </button>
        </form>
      </section>
    </div>
  );
}

// Helper Component
function StatTile({ label, value, formatter }) {
  // formatter (e.g. billing.total_paid's v => `$${v.toFixed(2)}`) assumes a
  // number - if the API ever omits the field instead of returning 0, this
  // guard avoids calling it with undefined/null and crashing the render.
  const displayValue = formatter && value !== undefined && value !== null ? formatter(value) : (value ?? "—");
  return (
    <article className="stat-tile">
      <p>{label}</p>
      <strong>{displayValue}</strong>
    </article>
  );
}
